import { AxiosResponse } from 'axios';
import { format } from 'date-fns';
import { applyOrgFilter } from '@/lib/activeOrg';
import { mapProfileToPerson } from '@/lib/mappers';
import { supabase } from '@/lib/supabase';
import { asResponse, paginationRange, buildPaginatedResponse } from '@/lib/supabase-helpers';
import { ResponseWithPagination } from '@/types';
import { PaginationParams, PersonType, TeamArea } from '@/types/api';
import {
  Contract,
  ContractStatus,
  ContractType,
  DirectReport,
  JobInfo,
  Seniority,
  StaffMember,
} from '@/types/api/staffMember';

// Map Supabase job_position → legacy JobInfo
function mapJobPosition(jp: any): JobInfo {
  return {
    id: jp.id,
    staffMemberId: jp.profile_id,
    teamId: jp.team_id,
    team: jp.teams
      ? ({
          id: jp.teams.id,
          name: jp.teams.name,
          area: jp.teams.area,
          category: jp.teams.category,
        } as any)
      : undefined,
    title: jp.title,
    seniority: jp.seniority,
    area: jp.area,
    directLeaderId: jp.leader_id,
    fromDate: jp.from_date,
    toDate: jp.to_date,
    createdAt: jp.created_at,
    updatedAt: jp.updated_at,
    directReports: (jp.job_position_direct_reports || []).map((dr: any) => ({
      id: dr.id,
      directReportId: dr.report_id,
      staffMemberJobId: dr.job_position_id,
      createdAt: dr.created_at,
      updatedAt: dr.created_at,
    })),
  };
}

// Build a StaffMember object from a profile + related data
async function buildStaffMember(profile: any): Promise<StaffMember> {
  // Fetch job positions for this profile
  const { data: jobPositions } = await supabase
    .from('job_positions')
    .select(
      `
      *,
      teams (id, name, area, category),
      job_position_direct_reports (id, report_id, job_position_id, created_at)
    `
    )
    .eq('profile_id', profile.id)
    .order('from_date', { ascending: false });

  // Fetch manager info if exists
  let manager: PersonType | undefined;
  if (profile.manager_id) {
    const { data: managerProfile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', profile.manager_id)
      .single();
    if (managerProfile) {
      manager = mapProfileToPerson(managerProfile);
    }
  }

  // Get the current team from the most recent job position
  const currentJob =
    (jobPositions || []).find((jp: any) => jp.is_current) || (jobPositions || [])[0];
  const team = currentJob?.teams
    ? {
        id: currentJob.teams.id,
        name: currentJob.teams.name,
        area: currentJob.teams.area,
        category: currentJob.teams.category,
      }
    : undefined;

  // Build contract from profile fields
  const contract: Contract | null = profile.hire_date
    ? {
        id: profile.id,
        staffMemberId: profile.id,
        hireDate: profile.hire_date,
        status: profile.contract_status || 'Full-time',
        type: profile.contract_type || 'Employee',
        createdAt: profile.created_at,
        updatedAt: profile.updated_at,
      }
    : null;

  // Fetch compensation history
  const { data: compensations } = await supabase
    .from('compensation_history')
    .select('*')
    .eq('profile_id', profile.id)
    .order('start_date', { ascending: false });

  return {
    id: profile.id,
    person: mapProfileToPerson(profile),
    manager,
    team: team as any,
    contract,
    hiringDate: profile.hire_date || '',
    lastBankOfHoursResetDate: profile.last_bank_hours_reset,
    jobInfo: (jobPositions || []).map(mapJobPosition),
    compensationHistory: (compensations || []).map((c: any) => ({
      id: c.id,
      personId: c.profile_id,
      person: profile as any as PersonType,
      hourlyRate: c.hourly_rate != null ? String(c.hourly_rate) : undefined,
      monthlyPayment: c.monthly_payment != null ? String(c.monthly_payment) : undefined,
      startDate: c.start_date,
      changeReason: c.change_reason || 'Other',
      createdAt: c.created_at,
      updatedAt: c.updated_at,
    })),
    createdAt: profile.created_at,
    updatedAt: profile.updated_at,
  };
}

const getStaffMemberByPersonId = async ({
  personId,
}: {
  personId: string;
}): Promise<AxiosResponse<StaffMember>> => {
  const { data: profile, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', personId)
    .single();

  if (error) throw error;
  const staffMember = await buildStaffMember(profile);
  return asResponse(staffMember);
};

const getTeamStaffMembers = async ({
  teamId,
}: {
  teamId: string;
}): Promise<AxiosResponse<StaffMember[]>> => {
  const { data: members, error } = await supabase
    .from('team_members')
    .select('profile_id')
    .eq('team_id', teamId);

  if (error) throw error;

  const profileIds = (members || []).map((m: any) => m.profile_id);
  if (!profileIds.length) return asResponse([]);

  const { data: profiles } = await supabase.from('profiles').select('*').in('id', profileIds);

  const staffMembers = await Promise.all((profiles || []).map((p: any) => buildStaffMember(p)));

  return asResponse(staffMembers);
};

const getStaffMember = async ({
  staffMemberId,
}: {
  staffMemberId: string;
}): Promise<AxiosResponse<StaffMember>> => {
  // In the new schema, staffMemberId IS profileId
  return getStaffMemberByPersonId({ personId: staffMemberId });
};

export type CreateStaffMemberByPersonIdProps = {
  personId: string;
  hiringDate: Date;
};

const createStaffMemberByPersonId = async ({
  personId,
  hiringDate,
}: CreateStaffMemberByPersonIdProps): Promise<AxiosResponse<StaffMember>> => {
  const { error } = await supabase
    .from('profiles')
    .update({
      hire_date: format(hiringDate, 'yyyy-MM-dd'),
      profile_type: 'employee',
      employee_work_status: 'active',
    })
    .eq('id', personId);

  if (error) throw error;
  return getStaffMemberByPersonId({ personId });
};

export type ListStaffMembersProps = PaginationParams & {
  term?: string;
};

export type ListStaffMembersResponse = ResponseWithPagination<StaffMember[]>;

const listStaffMembers = async ({
  take,
  page,
  term,
}: ListStaffMembersProps): Promise<AxiosResponse<ListStaffMembersResponse>> => {
  const [from, to] = paginationRange(page, take);

  let query = supabase
    .from('profiles')
    .select('*', { count: 'exact' })
    .in('profile_type', ['employee', 'both'])
    .is('deleted_at', null)
    .range(from, to)
    .order('first_name');

  query = applyOrgFilter(query);

  if (term) {
    query = query.or(`first_name.ilike.%${term}%,last_name.ilike.%${term}%,email.ilike.%${term}%`);
  }

  const { data: profiles, count, error } = await query;
  if (error) throw error;

  const staffMembers = await Promise.all((profiles || []).map((p: any) => buildStaffMember(p)));

  const totalCount = count || 0;

  return asResponse(
    buildPaginatedResponse(staffMembers, totalCount, page, take) as ListStaffMembersResponse
  );
};

export type CreateContractProps = {
  staffMemberId: string;
  hireDate: string;
  status: ContractStatus;
  type: ContractType;
};

const createContract = async ({
  staffMemberId,
  hireDate,
  status,
  type,
}: CreateContractProps): Promise<AxiosResponse<Contract>> => {
  const { data, error } = await supabase
    .from('profiles')
    .update({
      hire_date: hireDate,
      contract_status: status,
      contract_type: type,
    })
    .eq('id', staffMemberId)
    .select()
    .single();

  if (error) throw error;

  return asResponse({
    id: data.id,
    staffMemberId: data.id,
    hireDate: data.hire_date,
    status: data.contract_status,
    type: data.contract_type,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  } as Contract);
};

export type UpdateContractProps = {
  id: string;
  staffMemberId: string;
  hireDate?: string;
  status?: ContractStatus;
  type?: ContractType;
};

const updateContract = async ({
  staffMemberId,
  hireDate,
  status,
  type,
}: UpdateContractProps): Promise<AxiosResponse<Contract>> => {
  const updates: any = {};
  if (hireDate) updates.hire_date = hireDate;
  if (status) updates.contract_status = status;
  if (type) updates.contract_type = type;

  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', staffMemberId)
    .select()
    .single();

  if (error) throw error;

  return asResponse({
    id: data.id,
    staffMemberId: data.id,
    hireDate: data.hire_date,
    status: data.contract_status,
    type: data.contract_type,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  } as Contract);
};

export type CreateJobInfoProps = {
  staffMemberId: string;
  title: string;
  seniority: Seniority;
  area: TeamArea;
  teamId: string;
  directLeaderId: string;
  directReportsIds?: string[];
  fromDate: string;
  toDate?: string;
};

export type CrateJobInfoResponse = Pick<
  JobInfo,
  'area' | 'directLeaderId' | 'fromDate' | 'toDate' | 'seniority' | 'teamId' | 'title'
> & {
  directReportsIds: string[];
};

const createJobInfo = async ({
  staffMemberId,
  directReportsIds,
  directLeaderId,
  fromDate,
  toDate,
  ...payload
}: CreateJobInfoProps): Promise<AxiosResponse<CrateJobInfoResponse>> => {
  const { data: jp, error } = await supabase
    .from('job_positions')
    .insert({
      profile_id: staffMemberId,
      leader_id: directLeaderId,
      from_date: fromDate,
      to_date: toDate || null,
      ...payload,
      team_id: payload.teamId,
    })
    .select()
    .single();

  if (error) throw error;

  // Add direct reports if provided
  if (directReportsIds?.length) {
    await supabase.from('job_position_direct_reports').insert(
      directReportsIds.map((reportId) => ({
        job_position_id: jp.id,
        report_id: reportId,
      }))
    );
  }

  return asResponse({
    ...payload,
    directLeaderId,
    fromDate,
    toDate,
    directReportsIds: directReportsIds || [],
  } as CrateJobInfoResponse);
};

const listJobInfos = async ({
  staffMemberId,
}: {
  staffMemberId: string;
}): Promise<AxiosResponse<JobInfo[]>> => {
  const { data: jobPositions, error } = await supabase
    .from('job_positions')
    .select(
      `
      *,
      teams (id, name, area, category),
      job_position_direct_reports (id, report_id, job_position_id, created_at)
    `
    )
    .eq('profile_id', staffMemberId)
    .order('from_date', { ascending: false });

  if (error) throw error;
  return asResponse((jobPositions || []).map(mapJobPosition));
};

export type UpdateJobInfoProps = Partial<
  Omit<CreateJobInfoProps, 'staffMemberId' | 'directReportsIds'>
> & {
  staffMemberId: string;
  jobInfoId: string;
};

export type UpdateJobInfoResponse = Pick<
  JobInfo,
  | 'area'
  | 'createdAt'
  | 'directLeaderId'
  | 'fromDate'
  | 'id'
  | 'seniority'
  | 'staffMemberId'
  | 'teamId'
  | 'title'
  | 'toDate'
  | 'updatedAt'
>;

const updateJobInfo = async ({
  jobInfoId,
  staffMemberId,
  directLeaderId,
  fromDate,
  toDate,
  ...payload
}: UpdateJobInfoProps): Promise<AxiosResponse<UpdateJobInfoResponse>> => {
  const updates: any = { ...payload };
  if (directLeaderId !== undefined) updates.leader_id = directLeaderId;
  if (fromDate !== undefined) updates.from_date = fromDate;
  if (toDate !== undefined) updates.to_date = toDate;
  if (payload.teamId !== undefined) updates.team_id = payload.teamId;

  const { data, error } = await supabase
    .from('job_positions')
    .update(updates)
    .eq('id', jobInfoId)
    .select()
    .single();

  if (error) throw error;

  return asResponse({
    id: data.id,
    staffMemberId: data.profile_id,
    teamId: data.team_id,
    title: data.title,
    seniority: data.seniority,
    area: data.area,
    directLeaderId: data.leader_id,
    fromDate: data.from_date,
    toDate: data.to_date,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  } as UpdateJobInfoResponse);
};

export type RemoveDirectReportProps = {
  staffMemberId: string;
  jobInfoId: string;
  directReportId: string;
};

const removeDirectReport = async ({
  jobInfoId,
  directReportId,
}: RemoveDirectReportProps): Promise<AxiosResponse<never>> => {
  const { error } = await supabase
    .from('job_position_direct_reports')
    .delete()
    .eq('job_position_id', jobInfoId)
    .eq('report_id', directReportId);

  if (error) throw error;
  return asResponse(undefined as never);
};

export type DeleteJobInfoProps = {
  staffMemberId: string;
  jobInfoId: string;
};

const deleteJobInfo = async ({ jobInfoId }: DeleteJobInfoProps): Promise<AxiosResponse<never>> => {
  const { error } = await supabase.from('job_positions').delete().eq('id', jobInfoId);

  if (error) throw error;
  return asResponse(undefined as never);
};

export type AddDirectReportProps = {
  ownerStaffMemberId: string;
  jobInfoId: string;
  directReportId: string;
};

export type AddDirectReportResponse = Omit<DirectReport, 'staffMember'>;

const addDirectReport = async ({
  jobInfoId,
  directReportId,
}: AddDirectReportProps): Promise<AxiosResponse<AddDirectReportResponse>> => {
  const { data, error } = await supabase
    .from('job_position_direct_reports')
    .insert({
      job_position_id: jobInfoId,
      report_id: directReportId,
    })
    .select()
    .single();

  if (error) throw error;

  return asResponse({
    id: data.id,
    directReportId: data.report_id,
    staffMemberJobId: data.job_position_id,
    createdAt: data.created_at,
    updatedAt: data.created_at,
  } as AddDirectReportResponse);
};

export default {
  getStaffMemberByPersonId,
  getTeamStaffMembers,
  getStaffMember,
  createStaffMemberByPersonId,
  listStaffMembers,
  createContract,
  updateContract,
  createJobInfo,
  listJobInfos,
  updateJobInfo,
  removeDirectReport,
  addDirectReport,
  deleteJobInfo,
};
